import axios from 'axios';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';

import { Button } from '@/design-system/atoms/Button';
import { CardContent } from '@/design-system/atoms/Cards/CardContent';
import { CardImage } from '@/design-system/atoms/Cards/CardImage';
import { ContentContainer } from '@/design-system/atoms/ContentContainer';
import { Grid, GridColumn } from '@/design-system/atoms/Grid';
import { Title } from '@/design-system/atoms/Title';

import styles from './AllResources.module.scss';

const URL = '/bin/ahs/allresources';

/**
 * The All Resources component congregates all articles in a central location.
 */

const AllResources = ({
    id,
    hideImages,
    title = 'All Resources',
    loadMoreLabel = 'More Articles',
    excludeArticles,
    tagsInclude,
    tagsExclude,
    entityType,
    ...props
}) => {
    const [resources, setResources] = useState([]);
    const [page, setPage] = useState(1);
    const [moreAvailable, setMoreAvailable] = useState(false);

    /**
     * Fetches resources from the API.
     * @param {boolean} hideImages - Whether to hide images.
     * @param {boolean} excludeArticles - Whether to exclude articles.
     * @param {string[]} tagsExclude - Tags to exclude.
     * @param {string[]} tagsInclude - Tags to include.
     * @param {string} entityType - Entity type.
     * @param {number} page - Page number.
     * @param {AbortSignal} signal - Abort signal.
     * @returns {Promise} - The resources.
     */
    const getResources = async (
        hideImages,
        excludeArticles,
        tagsExclude,
        tagsInclude,
        entityType,
        page,
        signal = null,
    ) => {
        const params = [
            `tagsInclude=${tagsInclude}`,
            `tagsExclude=${tagsExclude}`,
            `entityType=${entityType}`,
            `excludeArticles=${excludeArticles}`,
        ]
            .filter((param) => param !== null)
            .join('&');

        try {
            const response = await axios.get(
                `${URL}?hideImages=${hideImages}&page=${page}${params ? `&${params}` : ''}`,
                { signal },
            );

            return response.data;
        } catch (error) {
            if (axios.isCancel(error)) {
                console.log('Request canceled:', error.message);
            } else {
                throw new Error(`Error: Status ${error.response?.status}, ${error.message}`);
            }
        }
    };

    /**
     * Triggers the getResources function and sets the resources state and moreAvailable state.
     * @param {AbortController} controller - Abort controller.
     * @returns {void}
     */
    const triggerGetResources = useCallback(
        async ({ controller, page }) => {
            try {
                const resourcesData = await getResources(
                    hideImages,
                    excludeArticles,
                    tagsExclude,
                    tagsInclude,
                    entityType,
                    page,
                    controller ? controller.signal : null,
                );

                const { cards, totalPages } = resourcesData;

                setResources((prevState) => {
                    const newResources = [];

                    if (Array.isArray(prevState)) {
                        newResources.push(...prevState);
                    }

                    if (Array.isArray(cards)) {
                        newResources.push(...cards);
                    }

                    return newResources.flat();
                });
                setMoreAvailable(totalPages > page);
            } catch (error) {
                console.error(error);
            }
        },
        [entityType, excludeArticles, hideImages, tagsExclude, tagsInclude],
    );

    /**
     * Handles the Load More button click event.
     */
    const clickHandler = async () => {
        setPage(page + 1);
    };

    // Get resources on page load and when the page number changes
    useEffect(() => {
        const controller = new AbortController();

        triggerGetResources({ controller, page });

        return () => controller.abort();
    }, [page, triggerGetResources]);

    return (
        <section className={styles[`all-resources`]} {...props}>
            <ContentContainer className={styles[`all-resources__container`]}>
                <Title
                    className={styles[`all-resources__title`]}
                    variant={Title.VARIANT.H3}
                    tag={Title.TAG.H2}
                    content={title}
                />
                <Grid className={styles[`all-resources__grid`]} col={{ sm: 4, md: 6, lg: 12 }}>
                    {resources?.map((resource, index) => (
                        <GridColumn
                            key={index}
                            className={styles[`all-resources__grid-column`]}
                            colSpan={{ sm: 4, md: 3, lg: 4 }}
                        >
                            {hideImages ? (
                                <CardContent
                                    {...resource}
                                    eyebrow={resource.tagTopic}
                                    analyticsActionLabel={resource.title}
                                    analyticsComponentTitle={resource.tagTopic}
                                    className={styles[`all-resources__card`]}
                                />
                            ) : (
                                <CardImage
                                    {...resource}
                                    eyebrow={resource.tagTopic}
                                    analyticsActionLabel={resource.title}
                                    analyticsComponentTitle={resource.tagTopic}
                                    image={{ defaultImgSrc: resource.image || '' }}
                                    className={styles[`all-resources__card`]}
                                />
                            )}
                        </GridColumn>
                    ))}
                </Grid>
                {moreAvailable && (
                    <div className={styles[`all-resources__button-wrapper`]}>
                        <Button
                            className={styles[`all-resources__cta-button`]}
                            label={loadMoreLabel}
                            ariaLabel={`${loadMoreLabel} - Content loads above`}
                            buttonStyle={Button.STYLE.SECONDARY}
                            onClick={clickHandler}
                            analytics={{ componentName: 'all_resources', componentTitle: title }}
                        />
                    </div>
                )}
            </ContentContainer>
        </section>
    );
};

AllResources.propTypes = {
    id: PropTypes.string,
    hideImages: PropTypes.bool,
    title: PropTypes.string,
    loadMoreLabel: PropTypes.string,
    excludeArticles: PropTypes.bool,
    tagsInclude: PropTypes.arrayOf(PropTypes.string),
    tagsExclude: PropTypes.arrayOf(PropTypes.string),
    entityType: PropTypes.string,
};

export default AllResources;
